import { useEffect, useRef } from 'react'

import { mod } from '../utils/number'

import { useVariableRef } from './useVariableRef'

/**
 * The {@link useSchedule} hook runs the passed `callback` on the specified
 * schedule. For example, at the start of every minute or hour.
 *
 * @usage
 * ```
 *  useSchedule(time, callback);
 * ```
 *
 * @param {number} time
 * @param {(number) => void} callback
 */
export function useSchedule(time, callback) {
  const callbackRef = useVariableRef(callback)
  const timeoutRef = useRef(null)

  useEffect(() => {
    function handleTimeout() {
      const now = Date.now()

      if (timeoutRef.current) {
        callbackRef.current?.(now)
        timeoutRef.current = setTimeout(handleTimeout, time - mod(now, time))
      }
    }

    clearTimeout(timeoutRef.current)

    if (isFinite(time)) {
      timeoutRef.current = setTimeout(
        handleTimeout,
        time - mod(Date.now(), time)
      )
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [callbackRef, time])
}
